<template>
  <div class="documents">
    <div class="documents__filters d-flex my-3">
      <datepicker
        id="selectedStartDate"
        v-model="selectedStartDate"
        :startDate="minDateForSelect"
        :endDate="maxDateForSelect"
      ></datepicker>
      <datepicker
        id="selectedEndDate"
        v-model="selectedEndDate"
        :startDate="minDateForSelect"
        :endDate="maxDateForSelect"
        class="ml-2"
      ></datepicker>
      <select class="form-control custom-select ml-2" v-model="selectedLayoutId" id="layoutId">
        <option v-for="layout in layouts" :key="layout.name" :value="layout.id" :selected="layout.id === selectedLayoutId">{{ layout.name }}</option>
      </select>
      <select class="form-control custom-select ml-2" v-model="isPaid" id="paid">
        <option v-for="paidOption in paidSelect" :key="paidOption.name" :value="paidOption.id" :selected="paidOption.id === isPaid">{{ paidOption.name }}</option>
      </select>
      <autocomplete id="companyName" class="ml-2" :suggestions="companyNames" :selectionText.sync="companyName" :selectionId.sync="companyId"></autocomplete>
      <button @click="()=> loadDocuments([])" class="btn btn-primary mx-3" :disabled="!isCanLoadDocumnets">Применить</button>
    </div>
    <div class="d-flex justify-content-end">
      <div class="input-group with-clear-icon mb-3 d-flex">
        <input type="text" class="form-control" v-model="searchText">
        <i class="ico ico-close" @click="clearSearchText" v-if="searchText.length > 0"></i>
        <div class="input-group-append">
          <span class="input-group-text">
            <i class="fa fa-search"></i>
          </span>
        </div>
      </div>
    </div>
    <div class="documents__table">
      <table class="table">
        <thead>
          <tr>
            <th
              class="table__th"
              v-for="column in tableColumns"
              :key="column.name"
              @click="columnClick(column.fieldName, column.isSortable)"
              :class="{
                'sort-asc':
                  column.isSortable &&
                  column.fieldName === sortFieldName &&
                  sortDirection === 'ASC',
                'sort-desc':
                  column.isSortable &&
                  column.fieldName === sortFieldName &&
                  sortDirection === 'DESC',
                'sort': column.isSortable && column.fieldName !== sortFieldName,
              }"
              :style="{
                width:
                  (column.width * 100) /
                    tableColumns.map((x) => x.width).reduce((a, b) => a + b) +
                  '%',
              }"
            >
              <div class="table-th">{{ column.name }}</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in tableRows"
            :key="row.id"
            class="table-tr"
            :class="{['table-tr--' + row.status]: row.status, 'table-tr--finished': row.isFinished}"
          >
            <td
              class="table__td"
              v-for="cell in row.cells"
              :key="cell.id"
              :data-name="cell.fieldName"
            >
              <div class="td-content td-content-lg">
                <template v-if="cell.fieldName === 'ctid'">
                  <router-link
                    :to="`/view-task/${cell.data.id}`"
                    :title="cell.data.name"
                  >
                    {{ cell.data.number }}
                  </router-link>
                </template>
                <template v-else-if="cell.fieldName === 'd_name'">
                  <a
                    target="_blank"
                    :href="`/api/documents/${cell.data.did}/pdf`"
                  >
                    {{ cell.data.value }}
                  </a>
                </template>
                <template v-else-if="cell.fieldName === 'actions'">
                  <button
                    @click="
                      showSendDocumentModal(
                        cell.data.did,
                        cell.data.name,
                        cell.data.email,
                        cell.data.leid
                      )
                    "
                    class="btn btn-sm btn-primary px-3"
                  >
                    <i class="ico ico-send-message"></i>
                  </button>
                  <button
                    @click="editDocument(cell.data.did)"
                    class="btn btn-sm btn-primary ml-2 px-3"
                  >
                    <i class="ico ico-pencil"></i>
                  </button>
                  <button
                    @click="deleteDocument(cell.data.did)"
                    class="btn btn-sm btn-primary ml-2 px-3"
                  >
                    <i class="ico ico-delete"></i>
                  </button>
                </template>
                <template v-else>
                  {{ cell.data }}
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import api from '../../api'
import taskStatuses from '../../helpers/taskStatuses'
import moment from 'moment'
import { debounce } from 'lodash'
import SendDocumentModal from './SendDocumentModal.vue'
import datepicker from '../../components/datepicker.vue'
import autocomplete from '../../components/autocoplete.vue'

const sortingLocalStorageKey = 'DOCUMENTS_SORTING_KEY'
const finishedTaskStatusId = taskStatuses.find(x => x.name === 'finished').id
const today = new Date()
const firstDateOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
const maxDate = new Date(new Date().setDate(today.getDate() + 1))
const minDate = new Date(new Date().setFullYear(today.getFullYear() - 2))

const columns = [
  {
    name: 'Дата документа',
    getCellData: (data) => {
      return data.datetime
    },
    isSortable: true,
    fieldName: 'datetime',
    width: 60
  },
  {
    name: 'Номер',
    getCellData: (data) => {
      return data.number
    },
    isSortable: true,
    fieldName: 'number',
    width: 60
  },
  {
    name: 'Сумма',
    getCellData: (data) => {
      return data.total_cost
    },
    isSortable: true,
    fieldName: 'total_cost',
    width: 60
  },
  {
    name: 'Клиент',
    getCellData: (data) => {
      return data.c_name
    },
    isSortable: true,
    fieldName: 'c_name',
    width: 60
  },
  {
    name: 'Отв. лицо',
    getCellData: (data) => {
      return data.fio
    },
    isSortable: true,
    fieldName: 'fio',
    width: 60
  },
  {
    name: 'Оплата',
    getCellData: (data) => {
      return data.cost
    },
    isSortable: true,
    fieldName: 'cost',
    width: 60
  },
  {
    name: 'Задача',
    getCellData: (data) => {
      return {
        number: data.ctid,
        id: data.tid,
        status: data.statuses,
        name: data.t_name
      }
    },
    isSortable: true,
    fieldName: 'ctid',
    width: 30
  },
  {
    name: 'Название документа',
    getCellData: (data) => {
      return {
        value: data.d_name,
        did: data.did
      }
    },
    isSortable: true,
    fieldName: 'd_name',
    width: 60
  },
  {
    name: 'Действия',
    getCellData: (data) => {
      return {
        name: data.d_name,
        did: data.did,
        email: data.c_email,
        leid: data.leid
      }
    },
    isSortable: false,
    fieldName: 'actions',
    width: 70
  }
]

export default {
  name: 'Documents',
  data () {
    return {
      maxDateForSelect: maxDate,
      minDateForSelect: minDate,
      selectedStartDate: firstDateOfMonth,
      selectedEndDate: today,
      layouts: [],
      selectedLayoutId: undefined,
      paidSelect: [
        { id: undefined, name: 'Все' },
        { id: true, name: 'Оплач. задачи' },
        { id: false, name: 'Не оплач. задачи' }
      ],
      isPaid: undefined,
      companyName: '',
      companyId: -1,
      companies: [],
      searchText: '',
      lazySearchText: '',
      columns,
      sortFieldName: columns[0].fieldName,
      sortDirection: 'DESC',
      tableRows: []
    }
  },
  components: {
    datepicker,
    autocomplete
  },
  async created () {
    this.loadSorting()
    await Promise.all([this.loadCompanies(), this.loadLayouts()])
    await this.loadDocuments([])
  },
  computed: {
    companyNames () {
      let result = [{ value: -1, text: 'Все клиенты', search: 'всеклиенты' }]
      result = result.concat(this.companies.map(x => { return { value: x.id, text: x.name, search: `${x.name}${x.email}${x.telephone}`.toLowerCase().replace(/[^a-zA-Zа-яА-Я0-9]+/g, '') } }))
      return result
    },
    selectedCompanyId () {
      const result = this.companies.find(x => x.id === this.companyId)
      if (result) {
        return result.id
      }
      return undefined
    },
    tableColumns () {
      return columns.map((x) => ({
        name: x.name,
        fieldName: x.fieldName,
        isSortable: x.isSortable,
        width: x.width
      }))
    },
    isCanLoadDocumnets () {
      return this.selectedStartDate && this.selectedEndDate && moment(this.selectedStartDate, 'DD-MM-YYYY') <= moment(this.selectedEndDate, 'DD-MM-YYYY')
    },
    filters () {
      return {
        startTime: moment(this.selectedStartDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        endTime: moment(this.selectedEndDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        paid: this.isPaid,
        leid: this.selectedCompanyId,
        lid: this.selectedLayoutId
      }
    }
  },
  watch: {
    searchText: debounce(function (newValue) {
      this.lazySearchText = newValue.toLowerCase()
    }, 300),
    async lazySearchText (newValue) {
      await this.loadDocuments([])
    }
  },
  methods: {
    async loadDocuments (previousTableRows) {
      try {
        const sort = { [this.sortFieldName]: this.sortDirection }
        const find = this.lazySearchText
        const filter = this.filters
        const response = await api.post.getDocuments({ sort, filter, find })
        const tableRows = this.getRows(response.data.data.documents)
        this.tableRows = previousTableRows.concat(tableRows)
      } catch {
      }
    },
    async loadCompanies () {
      try {
        const response = await api.get.companies()
        this.companies = response.data.data.legalEntities
      } catch (ex) {
        this.$notify({
          message: 'Произошла ошибка',
          type: 'danger'
        })
      }
    },
    async loadLayouts () {
      try {
        const response = await api.get.documentLayouts()
        this.layouts = [{ id: undefined, name: 'Все шаблоны' }, ...response.data.data.layouts]
      } catch (ex) {
        this.$notify({
          message: 'Произошла ошибка',
          type: 'danger'
        })
      }
    },
    getStatus (document) {
      if (+document.paid) {
        return 'paid'
      } else if (+document.t_status === finishedTaskStatusId) {
        return 'notPaid'
      } else if (+document.partially_paid) {
        return 'partiallyPaid'
      } else {
        return ''
      }
    },
    getRows (documents) {
      return documents.map((x) => {
        return {
          cells: columns.map((y) => {
            return {
              data: y.getCellData(x),
              id: y.name,
              fieldName: y.fieldName
            }
          }),
          id: x.did,
          status: this.getStatus(x),
          isFinished: +x.t_status === finishedTaskStatusId
        }
      })
    },
    async columnClick (fieldName, isSortable) {
      if (!isSortable) return

      if (fieldName === this.sortFieldName) {
        this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC'
      } else {
        this.sortFieldName = fieldName
        this.sortDirection = 'ASC'
      }
      await this.loadDocuments([])
      this.saveSorting()
    },
    async showSendDocumentModal (id, name, email, leid) {
      let companyEmails = [{ email, isDefault: true }]

      if (leid) {
        try {
          const resp = await api.get.companyClients(leid)
          const companyClientEmails = resp.data.data.clients
            .filter(x => x.email)
            .map(x => ({ email: x.email, isDefault: false }))
          companyEmails = companyEmails.concat(companyClientEmails)
        } catch {}
      }

      const modalProps = {
        companyEmails,
        document: { id, name }
      }

      this.$modal.show(
        SendDocumentModal,
        {
          ...modalProps
        },
        {
          height: 'auto'
        }
      )
    },
    deleteDocument (id) {
      this.$modal.show('dialog', {
        title: 'Внимание!',
        text: 'Вы уверены что хотите удалить?',
        buttons: [
          {
            title: 'Нет'
          },
          {
            title: 'Да',
            default: true,
            handler: async () => {
              try {
                await api.delete.document(id)
                this.tableRows = this.tableRows.filter((x) => x.id !== id)
              } catch {
                // ignore
              }
              this.$modal.hide('dialog')
            }
          }
        ]
      })
    },
    editDocument (id) {
      this.$router.push(`/document-editor/${id}`)
    },
    saveSorting () {
      localStorage.setItem(sortingLocalStorageKey, JSON.stringify({ sortFieldName: this.sortFieldName, sortDirection: this.sortDirection }))
    },
    loadSorting () {
      const sorting = localStorage.getItem(sortingLocalStorageKey)
      if (sorting) {
        const parsedSorting = JSON.parse(sorting)
        this.sortFieldName = parsedSorting.sortFieldName
        this.sortDirection = parsedSorting.sortDirection
      }
    },
    clearSearchText () {
      this.searchText = ''
    }
  }
}
</script>

<style lang="scss">
.documents {
  &__filters {
    .form-control{
      max-width: 180px;
    }
  }

  .with-clear-icon {
    max-width: 320px;
  }

  &__table {
    .table__th {
      position: sticky;
      top: 50px;
      z-index: 1;
    }
  }

  .table {
    .table-tr {
      &--paid {
        td[data-name="ctid"] {
          .td-content {
            background-color: #28bd8b;
            color: #fff;
            a {
              color: #fff;
            }
          }
        }

        td {
          background-color: lighten($color: #28bd8b, $amount: 40);
        }
      }

      &--notPaid {
        td[data-name="ctid"] {
          .td-content {
            background-color: #e84c3d;
            color: #fff;
            a {
              color: #fff;
            }
          }
        }

        td {
          background-color: lighten($color: #e84c3d, $amount: 30);
        }
      }

      &--partiallyPaid {
        td[data-name="ctid"] {
          .td-content {
            background-color: #408fc2;
            color: #fff;
            a {
              color: #fff;
            }
          }
        }

        td {
          background-color: lighten($color: #408fc2, $amount: 30);
        }
      }

      &--finished {
         td {
           .td-content{
            color: #7b7c7d;
           }
        }
      }
    }

    td[data-name="ctid"] {
      .td-content {
        display: flex;
        justify-content: center;
        width: 42px;
        height: 42px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 700;
        background-color: #c1c1c2;
        color: #7b7c7d;
        cursor: pointer;

        a {
          color: #7b7c7d;
          text-decoration: none;
          height: 40px;
          line-height: 40px;
          width: 100%;
          text-align: center;
        }
      }
    }
  }
}
</style>
